/**
 * Checkout Store
 *
 * Estado global do checkout usando Zustand.
 * Gerencia steps, formularios e selecoes do checkout.
 *
 * @since 2025-12-16
 * @epic INIT-0045
 * @module tmf_store
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ShipmentAddressData } from '@/types/store';

// ==========================================
// TYPES
// ==========================================

export type CheckoutStep = 'cart' | 'address' | 'shipping' | 'payment' | 'review' | 'confirmation';

interface AddressForm extends ShipmentAddressData {
  // Campos adicionais do formulario
  saveAddress?: boolean;
  isDefault?: boolean;
}

interface CheckoutState {
  // Navegacao
  currentStep: CheckoutStep;
  completedSteps: CheckoutStep[];

  // Cart
  cartId: number | null;
  storeId: number | null;

  // Endereco
  shippingAddress: AddressForm | null;
  billingAddress: AddressForm | null;
  useSameAddress: boolean;

  // Frete
  selectedShippingMethodId: number | null;
  shippingCost: number;
  estimatedDeliveryDays: number | null;

  // Pagamento
  selectedPaymentMethodId: number | null;

  // Pessoa/Cliente
  personId: number | null;

  // Observacoes
  notes: string;

  // Resultado
  saleId: number | null;
  orderNumber: string | null;

  // UI
  isProcessing: boolean;
  error: string | null;

  // Actions - Navegacao
  setStep: (step: CheckoutStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  markStepCompleted: (step: CheckoutStep) => void;
  isStepCompleted: (step: CheckoutStep) => boolean;
  canProceedTo: (step: CheckoutStep) => boolean;

  // Actions - Cart
  setCartId: (cartId: number) => void;
  setStoreId: (storeId: number) => void;

  // Actions - Endereco
  setShippingAddress: (address: AddressForm) => void;
  setBillingAddress: (address: AddressForm | null) => void;
  setUseSameAddress: (useSame: boolean) => void;
  clearAddresses: () => void;

  // Actions - Frete
  selectShippingMethod: (methodId: number, cost: number, days?: number) => void;
  clearShippingMethod: () => void;

  // Actions - Pagamento
  selectPaymentMethod: (methodId: number) => void;
  clearPaymentMethod: () => void;

  // Actions - Pessoa
  setPersonId: (personId: number | null) => void;

  // Actions - Observacoes
  setNotes: (notes: string) => void;

  // Actions - Resultado
  setOrderResult: (saleId: number, orderNumber?: string) => void;

  // Actions - UI
  setProcessing: (processing: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
  startNewCheckout: (cartId: number, storeId: number) => void;
}

// ==========================================
// CONSTANTS
// ==========================================

const STEPS_ORDER: CheckoutStep[] = [
  'cart',
  'address',
  'shipping',
  'payment',
  'review',
  'confirmation',
];

// ==========================================
// INITIAL STATE
// ==========================================

const initialState = {
  currentStep: 'cart' as CheckoutStep,
  completedSteps: [] as CheckoutStep[],
  cartId: null,
  storeId: null,
  shippingAddress: null,
  billingAddress: null,
  useSameAddress: true,
  selectedShippingMethodId: null,
  shippingCost: 0,
  estimatedDeliveryDays: null,
  selectedPaymentMethodId: null,
  personId: null,
  notes: '',
  saleId: null,
  orderNumber: null,
  isProcessing: false,
  error: null,
};

// ==========================================
// STORE
// ==========================================

export const useCheckoutStore = create<CheckoutState>()(
  persist(
    (set, get) => ({
      // Initial State
      ...initialState,

      // Navegacao Actions
      setStep: (step) => set({ currentStep: step, error: null }),

      nextStep: () => {
        const { currentStep, completedSteps } = get();
        const currentIndex = STEPS_ORDER.indexOf(currentStep);

        if (currentIndex < STEPS_ORDER.length - 1) {
          // Marca step atual como completo
          if (!completedSteps.includes(currentStep)) {
            set({
              completedSteps: [...completedSteps, currentStep],
              currentStep: STEPS_ORDER[currentIndex + 1],
              error: null,
            });
          } else {
            set({
              currentStep: STEPS_ORDER[currentIndex + 1],
              error: null,
            });
          }
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        const currentIndex = STEPS_ORDER.indexOf(currentStep);

        if (currentIndex > 0) {
          set({
            currentStep: STEPS_ORDER[currentIndex - 1],
            error: null,
          });
        }
      },

      markStepCompleted: (step) =>
        set((state) => {
          if (!state.completedSteps.includes(step)) {
            return { completedSteps: [...state.completedSteps, step] };
          }
          return state;
        }),

      isStepCompleted: (step) => get().completedSteps.includes(step),

      canProceedTo: (step) => {
        const state = get();
        const targetIndex = STEPS_ORDER.indexOf(step);

        // Pode sempre ir para o primeiro step
        if (targetIndex === 0) return true;

        // Verifica se todos os steps anteriores estao completos
        for (let i = 0; i < targetIndex; i++) {
          if (!state.completedSteps.includes(STEPS_ORDER[i])) {
            return false;
          }
        }

        return true;
      },

      // Cart Actions
      setCartId: (cartId) => set({ cartId }),
      setStoreId: (storeId) => set({ storeId }),

      // Endereco Actions
      setShippingAddress: (address) =>
        set((state) => ({
          shippingAddress: address,
          billingAddress: state.useSameAddress ? address : state.billingAddress,
        })),

      setBillingAddress: (address) => set({ billingAddress: address }),

      setUseSameAddress: (useSame) =>
        set((state) => ({
          useSameAddress: useSame,
          billingAddress: useSame ? state.shippingAddress : state.billingAddress,
        })),

      clearAddresses: () =>
        set({
          shippingAddress: null,
          billingAddress: null,
          useSameAddress: true,
        }),

      // Frete Actions
      selectShippingMethod: (methodId, cost, days) =>
        set({
          selectedShippingMethodId: methodId,
          shippingCost: cost,
          estimatedDeliveryDays: days ?? null,
        }),

      clearShippingMethod: () =>
        set({
          selectedShippingMethodId: null,
          shippingCost: 0,
          estimatedDeliveryDays: null,
        }),

      // Pagamento Actions
      selectPaymentMethod: (methodId) =>
        set({ selectedPaymentMethodId: methodId }),

      clearPaymentMethod: () => set({ selectedPaymentMethodId: null }),

      // Pessoa Actions
      setPersonId: (personId) => set({ personId }),

      // Observacoes Actions
      setNotes: (notes) => set({ notes }),

      // Resultado Actions
      setOrderResult: (saleId, orderNumber) =>
        set({
          saleId,
          orderNumber: orderNumber ?? `#${saleId}`,
          currentStep: 'confirmation',
          completedSteps: STEPS_ORDER.slice(0, -1), // Todos menos confirmation
        }),

      // UI Actions
      setProcessing: (processing) => set({ isProcessing: processing }),
      setError: (error) => set({ error }),

      // Reset Actions
      reset: () => set(initialState),

      startNewCheckout: (cartId, storeId) =>
        set({
          ...initialState,
          cartId,
          storeId,
          currentStep: 'address',
          completedSteps: ['cart'],
        }),
    }),
    {
      name: 'tmf-checkout-storage',
      storage: createJSONStorage(() => sessionStorage), // Usa sessionStorage para nao persistir entre sessoes
      partialize: (state) => ({
        // Persistir apenas dados do checkout em andamento
        cartId: state.cartId,
        storeId: state.storeId,
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        shippingAddress: state.shippingAddress,
        billingAddress: state.billingAddress,
        useSameAddress: state.useSameAddress,
        selectedShippingMethodId: state.selectedShippingMethodId,
        shippingCost: state.shippingCost,
        selectedPaymentMethodId: state.selectedPaymentMethodId,
        personId: state.personId,
        notes: state.notes,
      }),
    }
  )
);

// ==========================================
// SELECTORS
// ==========================================

export const checkoutSelectors = {
  // Progresso
  getProgress: (state: CheckoutState): number => {
    const currentIndex = STEPS_ORDER.indexOf(state.currentStep);
    return Math.round((currentIndex / (STEPS_ORDER.length - 1)) * 100);
  },

  // Validacao por step
  isAddressValid: (state: CheckoutState): boolean => {
    if (!state.shippingAddress) return false;
    const addr = state.shippingAddress;
    return !!(addr.name && addr.city && addr.state && addr.postal_code);
  },

  isShippingValid: (state: CheckoutState): boolean =>
    state.selectedShippingMethodId !== null,

  isPaymentValid: (state: CheckoutState): boolean =>
    state.selectedPaymentMethodId !== null,

  isReadyForReview: (state: CheckoutState): boolean =>
    checkoutSelectors.isAddressValid(state) &&
    checkoutSelectors.isShippingValid(state) &&
    checkoutSelectors.isPaymentValid(state),

  // Resumo
  getCheckoutSummary: (state: CheckoutState) => ({
    hasAddress: state.shippingAddress !== null,
    hasShipping: state.selectedShippingMethodId !== null,
    hasPayment: state.selectedPaymentMethodId !== null,
    shippingCost: state.shippingCost,
    isComplete: state.saleId !== null,
  }),
};

// ==========================================
// HELPERS
// ==========================================

export const CHECKOUT_STEPS = STEPS_ORDER;

export function getStepLabel(step: CheckoutStep): string {
  const labels: Record<CheckoutStep, string> = {
    cart: 'Carrinho',
    address: 'Endereco',
    shipping: 'Entrega',
    payment: 'Pagamento',
    review: 'Revisao',
    confirmation: 'Confirmacao',
  };
  return labels[step];
}

export function getStepIndex(step: CheckoutStep): number {
  return STEPS_ORDER.indexOf(step);
}
